<script setup>
import { useLayout } from '@/layout/composables/layout';
import { computed, defineAsyncComponent, onMounted, onUnmounted, provide, ref, watch } from 'vue';
import AppSidebar from './AppSidebar.vue';
const MobileAppChrome = defineAsyncComponent(() => import('./MobileAppChrome.vue'));
import { mobileAccountKey, useMobileLayout } from '@/composables/useMobileLayout.js';
import NotificationManager from '@/components/NotificationManager.vue';
const IPhonePreparedRoutes = defineAsyncComponent(() => import('./IPhonePreparedRoutes.vue'));
import { useUserContext } from '@/composables/userContext.js';
import { botCreationLimitDialog } from '@/composables/useBotCreationLimit.js';
import { navigationRecovery } from '@/composables/useNavigationRecovery.js';
import { useDesktopBotsPrefetch } from '@/composables/useDesktopBotsPrefetch.js';
import NavigationRecoveryNotice from '@/components/NavigationRecoveryNotice.vue';
import botLimitIllustration from '@/assets/bot-limit-illustration.png';
import { flushDesktopPageScroll, rememberDesktopPageScroll } from '@/utils/desktopPageScroll.js';
const loadBotLimitDialog = () => import('@/components/bot/BotLimitDialog.vue');
const BotLimitDialog = defineAsyncComponent(loadBotLimitDialog);

let preloadHandle;
const desktopApp = Boolean(window.__CORECRY_ELASTIC_CONTENT__);
onMounted(() => {
    if (desktopApp) window.addEventListener('pagehide', flushDesktopPageScroll);
    void import('@/composables/useBotCreationLimitGate.js').catch(() => {});
    const preload = () => {
        void loadBotLimitDialog().catch(() => {});
        const illustration = new Image();
        illustration.src = botLimitIllustration;
        void illustration.decode().catch(() => {});
    };
    preloadHandle = window.requestIdleCallback ? window.requestIdleCallback(preload, { timeout: 2000 }) : window.setTimeout(preload, 1500);
});
onUnmounted(() => {
    if (desktopApp) { window.removeEventListener('pagehide', flushDesktopPageScroll); flushDesktopPageScroll(); }
    if (window.cancelIdleCallback) window.cancelIdleCallback(preloadHandle);
    else window.clearTimeout(preloadHandle);
});

const { layoutConfig, layoutState, isSidebarActive, toggleMenu } = useLayout();
const outsideClickListener = ref(null);
const sidebar = ref(null);
const { isMobile } = useMobileLayout();
const preparedMobile = computed(() => isMobile.value && window.__CORECRY_IOS_BUILD__?.preloadPages === true);
const { userContext } = useUserContext();
const cacheIdentity = computed(() => `${userContext.value?.user_id}:${Boolean(userContext.value?.is_owner)}:${Boolean(userContext.value?.switched_by_admin)}`);
const backgroundVisibility = computed(() => Object.fromEntries(Object.entries(sidebar.value?.pageVisibilitySettings || {}).map(([key, value]) =>
    [key, Boolean(value.visible_to_all || (userContext.value?.is_owner && value.visible_to_admin_only))])));
useDesktopBotsPrefetch(computed(() => backgroundVisibility.value['my-bots']));
provide(mobileAccountKey, action => {
    const handlers = { logout: 'handleLogout', switch: 'openSwitchUserModal', back: 'switchBackToAdmin' };
    sidebar.value?.[handlers[action]]?.();
});

watch(isSidebarActive, (newVal) => {
    if (newVal) {
        bindOutsideClickListener();
    } else {
        unbindOutsideClickListener();
    }
});

const containerClass = computed(() => {
    return {
        'layout-overlay': layoutConfig.menuMode === 'overlay',
        'layout-static': layoutConfig.menuMode === 'static',
        'layout-static-inactive': layoutState.staticMenuDesktopInactive && layoutConfig.menuMode === 'static',
        'layout-overlay-active': layoutState.overlayMenuActive,
        'layout-mobile-active': layoutState.staticMenuMobileActive,
        'sidebar-collapsed': layoutState.sidebarCollapsed
    };
});

function bindOutsideClickListener() {
    if (!outsideClickListener.value) {
        outsideClickListener.value = (event) => {
            try {
                if (isOutsideClicked(event)) {
                    layoutState.overlayMenuActive = false;
                    layoutState.staticMenuMobileActive = false;
                    layoutState.menuHoverActive = false;
                }
            } catch (error) {
                globalThis.clientLogger?.warn('Error in outside click listener:', error);
            }
        };
        document.addEventListener('click', outsideClickListener.value);
    }
}

function unbindOutsideClickListener() {
    if (outsideClickListener.value) {
        document.removeEventListener('click', outsideClickListener.value);
        outsideClickListener.value = null;
    }
}

function isOutsideClicked(event) {
    // Check if event and target exist
    if (!event || !event.target) {
        return false;
    }

    const sidebarEl = document.querySelector('.layout-sidebar-new');
    const topbarEl = document.querySelector('.layout-menu-button');

    // Check if elements exist and if the click is outside
    const isOutsideSidebar = !sidebarEl || (!sidebarEl.isSameNode(event.target) && !sidebarEl.contains(event.target));
    const isOutsideTopbar = !topbarEl || (!topbarEl.isSameNode(event.target) && !topbarEl.contains(event.target));

    return isOutsideSidebar && isOutsideTopbar;
}
</script>

<template>
    <div class="layout-wrapper page-bg" :class="containerClass">
        <!-- Mobile Menu Button -->
        <button
            class="mobile-menu-button layout-menu-button"
            @click="toggleMenu"
            v-show="layoutConfig.menuMode === 'overlay'"
        >
            <div class="mobile-menu-icon">
                <span></span>
                <span></span>
                <span></span>
            </div>
        </button>

        <app-sidebar ref="sidebar"></app-sidebar>
        <MobileAppChrome v-if="isMobile" :visibility="sidebar?.pageVisibilitySettings" :loaded="Boolean(sidebar?.pageVisibilityLoaded)" />
        
        <div class="layout-main-container" :class="{ 'desktop-content-scroll': desktopApp }" @scroll="desktopApp && rememberDesktopPageScroll($event.currentTarget)">
            <div class="layout-main">
                <router-view v-slot="{ Component, route }">
                    <IPhonePreparedRoutes v-if="preparedMobile && userContext" :key="cacheIdentity" :component="Component" :route="route" :visibility="backgroundVisibility" />
                    <component :is="Component" v-else />
                </router-view>
            </div>
        </div>
        <div class="layout-mask animate-fadein"></div>
    </div>
    <NotificationManager />
    <BotLimitDialog v-if="botCreationLimitDialog.open" :open="true" :count="botCreationLimitDialog.count" :limit="botCreationLimitDialog.limit" :unavailable="botCreationLimitDialog.unavailable" :message="botCreationLimitDialog.message" @close="botCreationLimitDialog.open = false" />
    <NavigationRecoveryNotice v-if="navigationRecovery.open" />
</template>

<style lang="scss" scoped>
</style>
